import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SerialNumber, SerialNumberUpdate, SerialNumbersImportStatus, PaginatedResponse } from 'src/app/models/device/device.model';
import { AuthService } from '../../authentication/auth.service';
import { BaseApiService } from '../../base-api.service';
import { TranslateFirebaseService } from '../../utils/translate-firebase.service';

@Injectable()
export class RestSerialService extends BaseApiService{

  constructor(httpClient: HttpClient,
    translate : TranslateFirebaseService,
    private authService : AuthService) {
    super(httpClient, translate);
  }

  // Helper method to extract pagination info from headers
  private extractPaginationInfo<T>(response: HttpResponse<T[]>, pageNumber: number, pageSize: number): PaginatedResponse<T> {
    const totalItems = parseInt(response.headers.get('X-Total-Items') || '0', 10);
    const totalPages = parseInt(response.headers.get('X-Total-Pages') || '0', 10);
    
    return {
      data: response.body || [],
      totalItems,
      totalPages,
      currentPage: pageNumber,
      pageSize
    };
  }

  //SERIAL APIs

  deleteSerial(id: number) {
    return this.httpClient.delete(super.getUpsApiHost() + "SerialNumbers/" + id, {headers : this.authService.upsHttpHeaders});
  }

  checkSerial(serial : string) : Observable<any>{
    return this.httpClient.get(super.getUpsApiHost() + "SerialNumbers/Validate/" + serial, {headers : this.authService.upsHttpHeaders});
  }

  createSerial(serial: SerialNumber): Observable<SerialNumber> {
    return this.httpClient.post<SerialNumber>(super.getUpsApiHost() + "SerialNumbers", serial, {headers : this.authService.upsHttpHeaders});
  }

  getSerialNumberById(id: string) : Observable<SerialNumber>{
    return this.httpClient.get<SerialNumber>(super.getUpsApiHost() + "SerialNumbers/" + id, {headers : this.authService.upsHttpHeaders});
  }

  getSerialNumberBySerialNumber(serial: String): Observable<SerialNumber>{
    return this.httpClient.get<SerialNumber>(super.getUpsApiHost() + "SerialNumbers/Search/" + serial, {headers : this.authService.upsHttpHeaders});
  }

  // Search serials with multiple filters and pagination
  searchSerials(
    pageNumber: number,
    pageSize: number,
    sn?: string, 
    articleCode?: string, 
    deviceTypeId?: number, 
    installed?: boolean,
    warrantyExpBefore?: Date,
    warrantyExpAfter?: Date,
    warrantyStatus?: string,
    countryCode?: string,
    familyId?: number
  ): Observable<PaginatedResponse<SerialNumber>> {
    let queryParams: string[] = [
      `pageNumber=${pageNumber}`,
      `pageSize=${pageSize}`
    ];

    if (sn !== undefined && sn !== null && sn !== '') {
      queryParams.push(`sn=${encodeURIComponent(sn)}`);
    }
    if (articleCode !== undefined && articleCode !== null && articleCode !== '') {
      queryParams.push(`articleCode=${encodeURIComponent(articleCode)}`);
    }
    if (deviceTypeId !== undefined && deviceTypeId !== null) {
      queryParams.push(`deviceTypeId=${deviceTypeId}`);
    }
    if (installed !== undefined && installed !== null) {
      queryParams.push(`installed=${installed}`);
    }
    if (warrantyExpBefore !== undefined && warrantyExpBefore !== null) {
      queryParams.push(`warrantyExpBefore=${warrantyExpBefore.toISOString()}`);
    }
    if (warrantyExpAfter !== undefined && warrantyExpAfter !== null) {
      queryParams.push(`warrantyExpAfter=${warrantyExpAfter.toISOString()}`);
    }
    if (warrantyStatus !== undefined && warrantyStatus !== null && warrantyStatus !== '') {
      queryParams.push(`warrantyStatus=${encodeURIComponent(warrantyStatus)}`);
    }
    if (countryCode !== undefined && countryCode !== null && countryCode !== '') {
      queryParams.push(`countryCode=${encodeURIComponent(countryCode)}`);
    }
    if (familyId !== undefined && familyId !== null) {
      queryParams.push(`familyId=${familyId}`);
    }

    const queryString = queryParams.join('&');
    const url = super.getUpsApiHost() + "SerialNumbers/SearchAdvanced?" + queryString;
    
    return this.httpClient.get<SerialNumber[]>(url, { 
      headers: this.authService.upsHttpHeaders,
      observe: 'response'
    }).pipe(
      map(response => this.extractPaginationInfo(response, pageNumber, pageSize))
    );
  }
  updateSerial(serialId : number, serial: SerialNumberUpdate) {
    return this.httpClient.put(super.getUpsApiHost() + "SerialNumbers/" + serialId, serial, {headers : this.authService.upsHttpHeaders});
  }

  getAllSerials(pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    const url = super.getUpsApiHost() + "SerialNumbers" + "?pageNumber=" + pageNumber + "&pageSize=" + pageSize;
    return this.httpClient.get<SerialNumber[]>(url, { 
      headers: this.authService.upsHttpHeaders,
      observe: 'response'
    }).pipe(
      map(response => this.extractPaginationInfo(response, pageNumber, pageSize))
    );
  }

  getPossibleSerials(pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    const url = super.getUpsApiHost() + "SerialNumbers/NotInstalled" + "?pageNumber=" + pageNumber + "&pageSize=" + pageSize;
    return this.httpClient.get<SerialNumber[]>(url, { 
      headers: this.authService.upsHttpHeaders,
      observe: 'response'
    }).pipe(
      map(response => this.extractPaginationInfo(response, pageNumber, pageSize))
    );
  }

  getInstalledSerials(pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    const url = super.getUpsApiHost() + "SerialNumbers/Installed" + "?pageNumber=" + pageNumber + "&pageSize=" + pageSize;
    return this.httpClient.get<SerialNumber[]>(url, { 
      headers: this.authService.upsHttpHeaders,
      observe: 'response'
    }).pipe(
      map(response => this.extractPaginationInfo(response, pageNumber, pageSize))
    );
  }

  getAllSerialsByArticleCodePaginated(articleCode: string, pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    const url = super.getUpsApiHost() + "SerialNumbers/ArticleCode/" + articleCode + "?pageNumber=" + pageNumber + "&pageSize=" + pageSize;
    return this.httpClient.get<SerialNumber[]>(url, { 
      headers: this.authService.upsHttpHeaders,
      observe: 'response'
    }).pipe(
      map(response => this.extractPaginationInfo(response, pageNumber, pageSize))
    );
  }

  getAllSerialsByDeviceTypePaginated(deviceTypeId: number, pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    const url = super.getUpsApiHost() + "SerialNumbers/DeviceType/" + deviceTypeId + "?pageNumber=" + pageNumber + "&pageSize=" + pageSize;
    return this.httpClient.get<SerialNumber[]>(url, { 
      headers: this.authService.upsHttpHeaders,
      observe: 'response'
    }).pipe(
      map(response => this.extractPaginationInfo(response, pageNumber, pageSize))
    );
  }

  // Legacy methods for backward compatibility (returns only data array)
  getAllSerialsPaginated(pageNumber: number, pageSize: number): Observable<SerialNumber[]> {
    return this.getAllSerials(pageNumber, pageSize).pipe(
      map(response => response.data)
    );
  }

  getPossibleSerialsPaginated(pageNumber: number, pageSize: number): Observable<SerialNumber[]> {
    return this.getPossibleSerials(pageNumber, pageSize).pipe(
      map(response => response.data)
    );
  }

  getInstalledSerialsPaginated(pageNumber: number, pageSize: number): Observable<SerialNumber[]> {
    return this.getInstalledSerials(pageNumber, pageSize).pipe(
      map(response => response.data)
    );
  }

  // Non-paginated methods (for backward compatibility)
  getAllSerialsByArticleCode(articleCode: string): Observable<SerialNumber[]> {
    return this.httpClient.get<SerialNumber[]>(super.getUpsApiHost() + "SerialNumbers/ArticleCode/" + articleCode, {headers : this.authService.upsHttpHeaders});
  }

  getAllSerialsByDeviceType(deviceTypeId: number): Observable<SerialNumber[]> {
    return this.httpClient.get<SerialNumber[]>(super.getUpsApiHost() + "SerialNumbers/DeviceType/" + deviceTypeId, {headers : this.authService.upsHttpHeaders});
  }

  uploadSerialNumbers(formData: FormData): Observable<any> {
    return this.httpClient.post<any>(super.getUpsApiHost() + "SerialNumbers/Import", formData, {headers : this.authService.upsHttpHeaders});
  }

  getLastImportStatus(): Observable<SerialNumbersImportStatus> {
    return this.httpClient.get<SerialNumbersImportStatus>(super.getUpsApiHost() + "SerialNumbers/Import/Status", {headers : this.authService.upsHttpHeaders});
  }

  // Serial Number Import APIs
  // Import Serial Numbers methods
  uploadSerialNumberFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(super.getUpsApiHost() + "SerialNumberImport/upload", formData, {headers: this.authService.upsHttpHeaders});
  }

  // Alias usato dal servizio di dominio (SerialNumberService)
  uploadCsvForImport(file: File): Observable<any> {
    return this.uploadSerialNumberFile(file);
  }

  pauseSerialNumberImport(): Observable<any> {
    return this.httpClient.post<any>(super.getUpsApiHost() + "SerialNumberImport/pause", {}, {headers: this.authService.upsHttpHeaders});
  }

  resumeSerialNumberImport(): Observable<any> {
    return this.httpClient.post<any>(super.getUpsApiHost() + "SerialNumberImport/resume", {}, {headers: this.authService.upsHttpHeaders});
  }

  abortSerialNumberImport(): Observable<any> {
    return this.httpClient.post<any>(super.getUpsApiHost() + "SerialNumberImport/abort", {}, {headers: this.authService.upsHttpHeaders});
  }

  // --- Alias methods expected by higher-level service (SerialNumberService) ---
  pauseImport(): Observable<any> { 
    return this.pauseSerialNumberImport().pipe(
      // After pause operation, trigger immediate status refresh
      tap(() => {
        // Small delay to allow backend to process the pause
        setTimeout(() => {
          this.getImportStatusPolling().subscribe();
        }, 100);
      })
    );
  }
  
  resumeImport(): Observable<any> { 
    return this.resumeSerialNumberImport().pipe(
      // After resume operation, trigger immediate status refresh
      tap(() => {
        // Small delay to allow backend to process the resume
        setTimeout(() => {
          this.getImportStatusPolling().subscribe();
        }, 100);
      })
    );
  }
  
  abortImport(): Observable<any> { 
    return this.abortSerialNumberImport().pipe(
      // After abort operation, trigger immediate status refresh
      tap(() => {
        // Small delay to allow backend to process the abort
        setTimeout(() => {
          this.getImportStatusPolling().subscribe();
        }, 100);
      })
    );
  }

  getImportStatusStream(): EventSource {
    // Append bearer token (if any) as query param because EventSource can't set custom auth headers
    const base = super.getUpsApiHost() + "SerialNumberImport/status-sse";
    const token = this.authService?.upsHttpHeaders?.get('Authorization')?.replace('Bearer ', '') || '';
    const url = base
    
    // Prima di creare l'EventSource, logghiamo l'URL per debug
    console.log('SSE-DEBUG: Creazione EventSource con URL:', url);
    
    // Testiamo la connessione prima di creare l'EventSource
    console.log('SSE-DEBUG: Test connessione HTTP preliminare...');
    fetch(url, { 
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'x-Backend-Endpoint': 'ups',
        'Authorization': 'Bearer ' + this.authService.accessToken,

      }
    })
    .then(response => {
      console.log('SSE-DEBUG: Test connessione completato:', response.status, response.statusText);
      
      // Stampiamo gli headers in modo compatibile
      const headerObj: {[key: string]: string} = {};
      response.headers.forEach((value, name) => {
        headerObj[name] = value;
      });
      console.log('SSE-DEBUG: Headers:', headerObj);
      
      // Per debug, proviamo a leggere la risposta
      response.text().then(text => {
        console.log('SSE-DEBUG: Esempio di risposta:', text.substring(0, 200)); // primi 200 caratteri
        
        // Analisi dettagliata della risposta
        if (text.length > 0) {
          console.log('SSE-DEBUG: Risposta non vuota, analisi:', {
            'Lunghezza': text.length,
            'Primi 10 caratteri (hex)': Array.from(text.substring(0, 10)).map(c => c.charCodeAt(0).toString(16)).join(' '),
            'Contiene "data:"': text.includes('data:'),
            'Contiene "{" e "}"': text.includes('{') && text.includes('}'),
            'Contiene \n\n': text.includes('\n\n')
          });
        } else {
          console.log('SSE-DEBUG: Risposta vuota');
        }
      });
    })
    .catch(error => {
      console.error('SSE-DEBUG: Test connessione fallito:', error);
    });
    
    // Creiamo l'EventSource con debug integrato
    console.log('SSE-DEBUG: Creazione EventSource...');
    let eventSource: EventSource;
    
    try {
      // Primo tentativo con withCredentials: true
      eventSource = new EventSource(url, { withCredentials: true });
      console.log('SSE-DEBUG: EventSource creato con withCredentials=true');
    } catch (error) {
      console.warn('SSE-DEBUG: Errore con withCredentials=true, riprovo con false:', error);
      eventSource = new EventSource(url, { withCredentials: false });
      console.log('SSE-DEBUG: EventSource creato con withCredentials=false');
    }
    
    // Aggiungiamo listener di diagnostica direttamente nell'EventSource
    const debugHandlers = {
      onopen: (event: Event) => {
        console.log('SSE-DEBUG: Evento onopen ricevuto:', event);
        console.log('SSE-DEBUG: readyState =', eventSource.readyState);

      },
      onmessage: (event: MessageEvent) => {
        console.log('SSE-DEBUG: Evento onmessage diretto ricevuto:', event.data);
      },
      onerror: (event: Event) => {
        console.log('SSE-DEBUG: Evento onerror ricevuto:', event);
        console.log('SSE-DEBUG: readyState =', eventSource.readyState);
      }
    };
    
    // Aggiungiamo i listener di debug
    eventSource.addEventListener('open', debugHandlers.onopen);
    eventSource.addEventListener('message', debugHandlers.onmessage);
    eventSource.addEventListener('error', debugHandlers.onerror);
    
    return eventSource;
  }

  downloadImported(): Observable<Blob> {
    return this.httpClient.get(super.getUpsApiHost() + "SerialNumberImport/download/imported", 
      { headers: this.authService.upsHttpHeaders, responseType: 'blob' });
  }

  downloadErrors(): Observable<Blob> {
    return this.httpClient.get(super.getUpsApiHost() + "SerialNumberImport/download/errors", 
      { headers: this.authService.upsHttpHeaders, responseType: 'blob' });
  }

  downloadRemaining(): Observable<Blob> {
    return this.httpClient.get(super.getUpsApiHost() + "SerialNumberImport/download/remaining", 
      { headers: this.authService.upsHttpHeaders, responseType: 'blob' });
  }

  enableDaytimeStop(): Observable<any> {
    return this.httpClient.post<any>(super.getUpsApiHost() + "SerialNumberImport/daytime/enable", {}, {headers : this.authService.upsHttpHeaders});
  }

  disableDaytimeStop(): Observable<any> {
    return this.httpClient.post<any>(super.getUpsApiHost() + "SerialNumberImport/daytime/disable", {}, {headers : this.authService.upsHttpHeaders});
  }

  getDaytimeStatus(): Observable<any> {
    return this.httpClient.get<any>(super.getUpsApiHost() + "SerialNumberImport/daytime/status", {headers : this.authService.upsHttpHeaders});
  }

  // Metodo alternativo per ottenere lo stato dell'importazione tramite polling HTTP
  getImportStatusPolling(lastUpdate: Date | null = null): Observable<any> {
    let url = super.getUpsApiHost() + "SerialNumberImport/status-sse";
    
    // Aggiungiamo il parametro lastUpdate se presente
    if (lastUpdate) {
      url += `?lastUpdate=${lastUpdate.toISOString()}`;
    }
    
    return this.httpClient.get<any>(url, { headers: this.authService.upsHttpHeaders });
  }
  
  // Metodo per leggere direttamente lo stream SSE usando fetch API invece di EventSource
  // Questo può aggirare alcuni problemi di compatibilità con implementazioni non standard
  readSseStreamWithFetch(callback: (data: any) => void, onError?: (error: any) => void): { 
    stop: () => void 
  } {
    const base = super.getUpsApiHost() + "SerialNumberImport/status-sse";
    const url =  base;

    console.log('FETCH-SSE: Inizio lettura stream con fetch API:', url);
    
    let abortController = new AbortController();
    let isCancelled = false;
    
    const processStream = async () => {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            'x-Backend-Endpoint': 'ups',
            'Authorization': 'Bearer ' + this.authService.accessToken,
           
          },
          credentials: 'include',
          signal: abortController.signal
        });
        
        console.log('FETCH-SSE: Connessione stabilita:', response.status, response.statusText);
        
        if (!response.ok) {
          console.error('FETCH-SSE: Errore server', response.status, response.statusText);
          if (onError) onError({
            type: 'server_error',
            status: response.status,
            statusText: response.statusText
          });
          return;
        }
        
        if (!response.body) {
          console.error('FETCH-SSE: ReadableStream non supportato');
          if (onError) onError({ type: 'stream_not_supported' });
          return;
        }
        
        // Leggiamo il corpo della risposta come stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        console.log('FETCH-SSE: Stream pronto, inizio lettura dati...');
        
        // Leggiamo i chunk in arrivo
        while (!isCancelled) {
          const { value, done } = await reader.read();
          
          if (done) {
            console.log('FETCH-SSE: Stream terminato dal server');
            break;
          }
          
          // Decodifica i dati binari in testo
          const chunk = decoder.decode(value, { stream: true });
          console.log('FETCH-SSE: Chunk ricevuto:', chunk.length, 'bytes');
          
          if (chunk.length > 0) {
            console.log('FETCH-SSE: Contenuto chunk (primi 100 char):', chunk.substring(0, 100));
          }
          
          // Aggiungi al buffer e processa eventuali messaggi completi
          buffer += chunk;
          
          // Cerchiamo la sequenza che delimita i messaggi
          // Potrebbero essere "\n\n", "data:", o altri formati a seconda del server
          let messages = [];
          
          // Proviamo diversi delimitatori
          if (buffer.includes('\n\n')) {
            // Formato standard EventSource: messaggi separati da doppi newline
            messages = buffer.split('\n\n');
            buffer = messages.pop() || ''; // l'ultimo elemento potrebbe essere incompleto
          } else if (buffer.includes('data:')) {
            // Formato con prefisso "data:"
            messages = buffer.split('data:');
            buffer = messages.pop() || ''; // l'ultimo elemento potrebbe essere incompleto
            // Rimuovi il primo elemento vuoto se esiste
            if (messages.length > 0 && !messages[0].trim()) messages.shift();
          } else if (buffer.includes('}{')) {
            // JSON concatenati (formato non standard)
            // Split based on }{ boundaries and reconstruct valid JSON objects
            let parts = buffer.split('}{');
            buffer = parts.pop() || '';
            
            for (let i = 0; i < parts.length; i++) {
              let part = parts[i];
              if (i === 0) {
                part += '}'; // Aggiungi la chiusura del primo oggetto
              } else if (i === parts.length - 1) {
                part = '{' + part; // Aggiungi l'apertura dell'ultimo oggetto
              } else {
                part = '{' + part + '}'; // Ricostruisci oggetti in mezzo
              }
              messages.push(part);
            }
          }
          
          // Processa i messaggi trovati
          for (const msg of messages) {
            if (msg.trim()) {
              try {
                console.log('FETCH-SSE: Elaborazione messaggio:', msg);
                
                // Rimuovi eventuali prefissi (data:, event:, etc.)
                let data = msg.trim();
                if (data.startsWith('data:')) {
                  data = data.substring(5).trim();
                }
                
                // Rimuovi caratteri non necessari alla fine
                data = data.replace(/\n+$/, '');
                
                // Prova a parsare come JSON
                try {
                  const jsonData = JSON.parse(data);
                  console.log('FETCH-SSE: JSON valido:', jsonData);
                  callback(jsonData);
                } catch (jsonError) {
                  // Se non è JSON valido, prova a pulire il formato
                  console.log('FETCH-SSE: JSON non valido, provo a ripulire:', data);
                  
                  // Sostituisci single quote con double quote, ecc
                  const cleanData = data
                    .replace(/'/g, '"')
                    .replace(/(\w+):/g, '"$1":')
                    .replace(/"\{/g, '{')
                    .replace(/\}"/g, '}');
                  
                  try {
                    const jsonData = JSON.parse(cleanData);
                    console.log('FETCH-SSE: JSON ripulito valido:', jsonData);
                    callback(jsonData);
                  } catch (e) {
                    console.error('FETCH-SSE: Impossibile parsare JSON:', e);
                    if (onError) onError({ 
                      type: 'parse_error', 
                      error: e, 
                      rawData: data 
                    });
                  }
                }
              } catch (e) {
                console.error('FETCH-SSE: Errore elaborazione messaggio:', e);
                if (onError) onError({ 
                  type: 'processing_error', 
                  error: e,
                  rawData: msg
                });
              }
            }
          }
        }
      } catch (error) {
        if (isCancelled) {
          console.log('FETCH-SSE: Stream annullato manualmente');
        } else {
          console.error('FETCH-SSE: Errore fetch:', error);
          if (onError) onError({ type: 'fetch_error', error });
        }
      }
    };
    
    // Avvia il processo di lettura dello stream
    processStream();
    
    // Restituisci una funzione per fermare lo stream
    return {
      stop: () => {
        console.log('FETCH-SSE: Stopping fetch stream');
        isCancelled = true;
        abortController.abort();
      }
    };
  }

  isImportInProgress(): Observable<any> {
    return this.httpClient.get<any>(super.getUpsApiHost() + "SerialNumberImport/import/in-progress", {headers : this.authService.upsHttpHeaders});
  }

  getSSEUrl(): string {
  const base = super.getUpsApiHost() + "SerialNumberImport/status-sse";
  const token = this.authService?.upsHttpHeaders?.get('Authorization')?.replace('Bearer ', '') || '';
  return  base; 
  }

}
